import { useEffect, useRef, useState } from "react";

import type { PersonalCustomEmoji } from "../../api/client";

type Props = {
  emoji: PersonalCustomEmoji;
  loadPreview?: (customEmojiId: string, signal?: AbortSignal) => Promise<Blob>;
  size?: "editor" | "picker" | "preview";
};

const prefersReducedMotion = () => typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;

export function CustomEmoji({ emoji, loadPreview, size = "picker" }: Props) {
  const animationRef = useRef<HTMLSpanElement | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "failed">("loading");
  const reducedMotion = prefersReducedMotion();

  useEffect(() => {
    setUrl(null);
    setState(loadPreview ? "loading" : "failed");
    if (!loadPreview) return;
    const controller = new AbortController();
    let objectUrl: string | null = null;
    let disposeAnimation: (() => void) | null = null;
    void loadPreview(emoji.customEmojiId, controller.signal).then(async (blob) => {
      if (controller.signal.aborted) return;
      if (emoji.format === "animated" && animationRef.current) {
        const compressed = /(?:gzip|tgsticker)/i.test(blob.type);
        if (compressed && !("DecompressionStream" in window)) {
          throw new Error("This browser cannot decode a compressed emoji preview");
        }
        const text = compressed
          ? await new Response(
              blob.stream().pipeThrough(new DecompressionStream("gzip"))
            ).text()
          : await blob.text();
        const json = JSON.parse(text) as Record<string, unknown>;
        const lottie = await import("lottie-web");
        if (controller.signal.aborted || !animationRef.current) return;
        const animation = lottie.default.loadAnimation({
          container: animationRef.current,
          renderer: "svg",
          loop: !reducedMotion,
          autoplay: !reducedMotion,
          animationData: json
        });
        const ready = () => {
          if (controller.signal.aborted) return;
          if (reducedMotion) animation.goToAndStop?.(0, true);
          setState("ready");
        };
        animation.addEventListener?.("DOMLoaded", ready);
        if (typeof animation.addEventListener !== "function") ready();
        disposeAnimation = () => {
          animation.removeEventListener?.("DOMLoaded", ready);
          animation.destroy();
        };
        return;
      }
      if (typeof URL.createObjectURL === "function") {
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
        setState("ready");
      }
    }).catch(() => {
      if (!controller.signal.aborted) setState("failed");
    });
    return () => {
      controller.abort();
      disposeAnimation?.();
      if (objectUrl && typeof URL.revokeObjectURL === "function") URL.revokeObjectURL(objectUrl);
    };
  }, [emoji.customEmojiId, emoji.format, loadPreview, reducedMotion]);

  const label = state === "ready"
    ? null
    : <span className="custom-emoji-fallback" aria-hidden="true">{emoji.fallback}</span>;
  if (emoji.format === "animated") {
    return <span className={`custom-emoji custom-emoji-size-${size}`} role="img" aria-label={emoji.fallback}>
      {label}
      <span ref={animationRef} className="custom-emoji-animation" aria-hidden="true" />
    </span>;
  }
  return (
    <span className={`custom-emoji custom-emoji-size-${size}`} role="img" aria-label={emoji.fallback}>
      {!url ? label : emoji.format === "video"
        ? <video src={url} muted loop autoPlay={!reducedMotion} preload="auto" playsInline aria-hidden="true" />
        : <img src={url} alt="" />}
    </span>
  );
}
